import type { FounderSubmenu } from "../../founder/founderNavigation";

export type FounderActionIconKind =
  | "view"
  | "edit"
  | "manage"
  | "security"
  | "document"
  | "activity"
  | "publish"
  | "pause"
  | "restore"
  | "history"
  | "search"
  | "cancel";

type IconProps = {
  className?: string;
};

export function resolveFounderActionIcon(item: FounderSubmenu): FounderActionIconKind {
  if (item.id === "health" || item.id === "activity" || item.id === "audit") {
    return "activity";
  }

  if (item.id === "security" || item.id === "login-security") {
    return "security";
  }

  if (item.id === "legal") {
    return "document";
  }

  if (item.supportsEdit || item.cardAction === "Edit") {
    return "edit";
  }

  if (item.cardAction === "Manage") {
    return "manage";
  }

  return "view";
}

export function resolveFounderActionLabel(kind: FounderActionIconKind): string {
  switch (kind) {
    case "edit":
      return "Edit";
    case "manage":
      return "Manage";
    case "security":
      return "Security";
    case "document":
      return "Documents";
    case "activity":
      return "Activity";
    case "publish":
      return "Publish update";
    case "pause":
      return "Suspend";
    case "restore":
      return "Restore";
    case "history":
      return "History";
    case "search":
      return "Search";
    case "cancel":
      return "Cancel";
    default:
      return "View";
  }
}

export function FounderActionIcon({
  kind,
  className = "",
}: {
  kind: FounderActionIconKind;
  className?: string;
}) {
  switch (kind) {
    case "edit":
      return <IconPencil className={className} />;
    case "manage":
      return <IconSliders className={className} />;
    case "security":
      return <IconShield className={className} />;
    case "document":
      return <IconDocument className={className} />;
    case "activity":
      return <IconPulse className={className} />;
    case "publish":
      return <IconPublish className={className} />;
    case "pause":
      return <IconPause className={className} />;
    case "restore":
      return <IconRestore className={className} />;
    case "history":
      return <IconHistory className={className} />;
    case "search":
      return <IconSearch className={className} />;
    case "cancel":
      return <IconCancel className={className} />;
    default:
      return <IconEye className={className} />;
  }
}

function IconEye({ className }: IconProps) {
  return (
    <svg aria-hidden="true" className={className} viewBox="0 0 24 24">
      <path
        d="M2.25 12s3.75-6.75 9.75-6.75S21.75 12 21.75 12s-3.75 6.75-9.75 6.75S2.25 12 2.25 12Z"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.75"
      />
      <circle cx="12" cy="12" fill="none" r="3" stroke="currentColor" strokeWidth="1.75" />
    </svg>
  );
}

function IconPencil({ className }: IconProps) {
  return (
    <svg aria-hidden="true" className={className} viewBox="0 0 24 24">
      <path
        d="M4 20.25h4.5L19.5 9.25a1.77 1.77 0 0 0 0-2.5L17.25 4.5a1.77 1.77 0 0 0-2.5 0L4 15.25V20.25Z"
        fill="none"
        stroke="currentColor"
        strokeLinejoin="round"
        strokeWidth="1.75"
      />
    </svg>
  );
}

function IconSliders({ className }: IconProps) {
  return (
    <svg aria-hidden="true" className={className} viewBox="0 0 24 24">
      <path d="M4 7h10" fill="none" stroke="currentColor" strokeLinecap="round" strokeWidth="1.75" />
      <path d="M18 7h2" fill="none" stroke="currentColor" strokeLinecap="round" strokeWidth="1.75" />
      <circle cx="16" cy="7" fill="none" r="2" stroke="currentColor" strokeWidth="1.75" />
      <path d="M4 17h2" fill="none" stroke="currentColor" strokeLinecap="round" strokeWidth="1.75" />
      <path d="M10 17h10" fill="none" stroke="currentColor" strokeLinecap="round" strokeWidth="1.75" />
      <circle cx="8" cy="17" fill="none" r="2" stroke="currentColor" strokeWidth="1.75" />
    </svg>
  );
}

function IconShield({ className }: IconProps) {
  return (
    <svg aria-hidden="true" className={className} viewBox="0 0 24 24">
      <path
        d="M12 3.5 5 6.25V11c0 4.2 2.95 7.95 7 9.25 4.05-1.3 7-5.05 7-9.25V6.25L12 3.5Z"
        fill="none"
        stroke="currentColor"
        strokeLinejoin="round"
        strokeWidth="1.75"
      />
    </svg>
  );
}

function IconDocument({ className }: IconProps) {
  return (
    <svg aria-hidden="true" className={className} viewBox="0 0 24 24">
      <path
        d="M8 4.5h5.2L18 9.3V19.5a1 1 0 0 1-1 1H8a1 1 0 0 1-1-1v-14a1 1 0 0 1 1-1Z"
        fill="none"
        stroke="currentColor"
        strokeLinejoin="round"
        strokeWidth="1.75"
      />
      <path d="M13 4.5V10H18" fill="none" stroke="currentColor" strokeLinejoin="round" strokeWidth="1.75" />
    </svg>
  );
}

function IconPulse({ className }: IconProps) {
  return (
    <svg aria-hidden="true" className={className} viewBox="0 0 24 24">
      <path
        d="M4 12h2.5l2-4 3 8 2.5-5 2 4H20"
        fill="none"
        stroke="currentColor"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="1.75"
      />
    </svg>
  );
}

function IconPublish({ className }: IconProps) {
  return (
    <svg aria-hidden="true" className={className} viewBox="0 0 24 24">
      <path
        d="M12 16V4m0 0 4 4m-4-4-4 4M5 20h14"
        fill="none"
        stroke="currentColor"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="1.75"
      />
    </svg>
  );
}

function IconPause({ className }: IconProps) {
  return (
    <svg aria-hidden="true" className={className} viewBox="0 0 24 24">
      <path d="M9 6v12M15 6v12" fill="none" stroke="currentColor" strokeLinecap="round" strokeWidth="1.75" />
    </svg>
  );
}

function IconRestore({ className }: IconProps) {
  return (
    <svg aria-hidden="true" className={className} viewBox="0 0 24 24">
      <path
        d="M6 7v4h4M18 17a7 7 0 0 0-11.5-2.5L6 11"
        fill="none"
        stroke="currentColor"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="1.75"
      />
    </svg>
  );
}

function IconHistory({ className }: IconProps) {
  return (
    <svg aria-hidden="true" className={className} viewBox="0 0 24 24">
      <path
        d="M12 8v4l2.5 2.5M12 21a9 9 0 1 0-8.66-5.5"
        fill="none"
        stroke="currentColor"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="1.75"
      />
    </svg>
  );
}

function IconSearch({ className }: IconProps) {
  return (
    <svg aria-hidden="true" className={className} viewBox="0 0 24 24">
      <circle cx="11" cy="11" fill="none" r="5.5" stroke="currentColor" strokeWidth="1.75" />
      <path d="M16 16l4 4" fill="none" stroke="currentColor" strokeLinecap="round" strokeWidth="1.75" />
    </svg>
  );
}

function IconCancel({ className }: IconProps) {
  return (
    <svg aria-hidden="true" className={className} viewBox="0 0 24 24">
      <path
        d="M7 7l10 10M17 7 7 17"
        fill="none"
        stroke="currentColor"
        strokeLinecap="round"
        strokeWidth="1.75"
      />
    </svg>
  );
}
